import React, { useState, useContext } from "react";
import { useMediaQuery } from "@mui/material";

import { SelectAnswerContext } from "../contexts/selectAnswerContext";
import SearchResultLink from "../features/SearchResultLink";
import searchEngineLogo from "../assets/searchEngineLogo.svg";
import { LangContext } from "../contexts/langContext";
import useShowingCorrectAnswer from "../hooks/useShowingCorrectAnswer";
import { SearchResultRenderProps } from "@common";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

interface RenderProps extends SearchResultRenderProps {
  readOnly: boolean;
  showCorrect: boolean;
}

const SearchEngineResults: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({
  query,
  correctLinkIndex,
  link1URL,
  link1Type,
  link1Title,
  link1MetaDesc,
  link2URL,
  link2Type,
  link2Title,
  link2MetaDesc,
  link3URL,
  link3Type,
  link3Title,
  link3MetaDesc,
  readOnly,
  showCorrect,
}) => {
  const { onSelectAnswer } = useContext(SelectAnswerContext);
  const { translateTranslationData, translatePageText } = useContext(LangContext);
  const [dark] = useState(window.matchMedia("(prefers-color-scheme: dark)"));
  const mobile = useMediaQuery("(max-width: 540px)");
  const showingCorrectAnswer = useShowingCorrectAnswer();
  const displayCorrectAnswers = showCorrect || showingCorrectAnswer;

  const pointsController = (index: any) => {
    if (index === correctLinkIndex) {
      onSelectAnswer("correct", { val1: 0, val2: 1, val3: 0 });
    } else {
      onSelectAnswer("incorrect", { val1: 0, val2: 0, val3: 1 });
    }
  };

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: dark ? "#1d2342" : "#fff",
        color: dark ? "#e5e5ea" : "#3e3e3e",
        borderRadius: 6,
        marginBottom: "2em",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: "6px 6px 0 0",
          padding: 10,
          backgroundColor: dark ? "#10142b" : "#fff",
        }}
      >
        <div style={{ marginTop: 3, marginLeft: 3 }}>
          <img src={searchEngineLogo} alt="Piggy Sökmotor" width="42" />
        </div>
        <div
          style={{
            width: "80%",
            display: "flex",
            height: 38,
            alignItems: "center",
            backgroundColor: dark ? "#212959" : "#fff",
            border: dark ? "none" : "1px solid gray",
            padding: "0 15px",
            borderRadius: 19,
            fontSize: "0.85em",
            cursor: "text",
          }}
        >
          {translateTranslationData(query)}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          paddingLeft: 20,
          alignItems: "center",
          fontSize: "0.75em",
          fontWeight: "bold",
          backgroundColor: dark ? "#10142b" : "#fff",
        }}
      >
        <div
          style={{
            color: "#7d9efa",
            padding: "7px 0",
            margin: "0 5px",
            borderBottom: "3px solid #7d9efa",
            cursor: "pointer",
          }}
        >
          {translatePageText("general.all")}
        </div>
        <div
          style={{
            padding: "7px 0",
            margin: "0 5px",
            borderBottom: "3px solid transparent",
            cursor: "pointer",
          }}
        >
          {translatePageText("general.images")}
        </div>
        <div
          style={{
            padding: "7px 0",
            margin: "0 5px",
            borderBottom: "3px solid transparent",
            cursor: "pointer",
          }}
        >
          {translatePageText("general.news")}
        </div>
      </div>
      <div
        style={{
          padding: 20,
        }}
      >
        <SearchResultLink
          key={1}
          title={link1Title}
          metaDesc={link1MetaDesc}
          url={link1URL}
          type={link1Type}
          index={1}
          correctLinkIndex={correctLinkIndex}
          dark={dark}
          mobile={mobile}
          pointsController={pointsController}
          readOnly={readOnly}
          showCorrect={displayCorrectAnswers}
        />
        <SearchResultLink
          key={2}
          title={link2Title}
          metaDesc={link2MetaDesc}
          url={link2URL}
          type={link2Type}
          index={2}
          correctLinkIndex={correctLinkIndex}
          dark={dark}
          mobile={mobile}
          pointsController={pointsController}
          readOnly={readOnly}
          showCorrect={displayCorrectAnswers}
        />
        <SearchResultLink
          key={3}
          title={link3Title}
          metaDesc={link3MetaDesc}
          url={link3URL}
          type={link3Type}
          index={3}
          correctLinkIndex={correctLinkIndex}
          dark={dark}
          mobile={mobile}
          pointsController={pointsController}
          readOnly={readOnly}
          showCorrect={displayCorrectAnswers}
        />
      </div>
    </div>
  );
};

export default SearchEngineResults;
